import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageShell, DataTable, StatusPill, Pill,
  LoadingBox, ErrorBox, InfiniteSentinel,
  useAdminApi, useInfiniteList,
  ActionButton, Field, Option, Note, Modal, Icon,
  DateCell, Modules, DeviceBar,
} from "./_adminApi";

export default function LicencesList() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [nonce, setNonce] = useState(0);
  const url = `admin/licences/?${q ? `q=${encodeURIComponent(q)}&` : ""}_=${nonce}`;
  const { items, loadMore, loading, err, hasMore, initialized, total } = useInfiniteList(url);

  const moduleList = (r) => {
    const list = [];
    if (r.is_hospital) list.push("H");
    if (r.is_store) list.push("S");
    if (r.is_lab) list.push("L");
    if (r.is_radiology_lab) list.push("R");
    return list;
  };

  return (
    <PageShell
      title="IMS Licences"
      subtitle="Every clinic, pharmacy, lab or store running the IMS desktop app. Click a row to open the full detail page with Devices, Events, Payments, Backups, Audit and Bugs."
      actions={
        <ActionButton icon={<Icon.plus />} onClick={() => setShowCreate(true)}>
          New licence
        </ActionButton>
      }
    >
      <Note>
        <strong>What is a licence?</strong> One row per customer running the IMS desktop app.
        Module chips: <Pill kind="bad" dot>H</Pill> Hospital,{" "}
        <Pill kind="teal" dot>S</Pill> Store,{" "}
        <Pill kind="blue" dot>L</Pill> Lab,{" "}
        <Pill kind="violet" dot>R</Pill> Radiology.
        Licence key and backup secret are auto-generated on create — you only need email + password.
      </Note>

      <div className="ad-card">
        <div className="ad-card-head">
          <div className="ad-tools" style={{ flex: 1 }}>
            <label className="ad-search">
              <Icon.search />
              <input placeholder="Search by email, store or city…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
          </div>
        </div>

        {err && <div style={{ padding: 16 }}><ErrorBox error={err} /></div>}
        {!initialized && !err && <LoadingBox />}
        {initialized && (
          <>
            <DataTable
              rows={items}
              emptyText="No licences match these filters."
              onRowClick={(r) => navigate(`/dashboard/admin/licences/${r.id}`)}
              columns={[
                { label: "Email", render: (r) => (
                  <div style={{ minWidth: 0 }}>
                    <div className="t-strong">{r.email || "(no email)"}</div>
                  </div>
                )},
                { label: "Store / Clinic", render: (r) => (
                  <div>
                    <div className="t-strong" style={{ fontWeight: 600 }}>{r.store_name || "—"}</div>
                    <div className="t-sub">
                      {r.store_city || ""}{r.store_type ? ` · ${r.store_type}` : ""}
                    </div>
                  </div>
                )},
                { label: "Modules", render: (r) => <Modules list={moduleList(r)} /> },
                { label: "Status", render: (r) => <StatusPill status={r.licence_status} /> },
                { label: "Devices", align: "right", render: (r) => (
                  <DeviceBar used={r.device_count ?? 0} max={r.max_devices ?? 1} />
                )},
                { label: "Last seen", render: (r) => <DateCell iso={r.last_seen} /> },
              ]}
            />
            <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
          </>
        )}

        {initialized && items.length > 0 && (
          <div className="ad-tfoot">
            <span>Showing <b style={{ color: "var(--fg-1)" }}>{items.length}</b>{typeof total === "number" ? ` of ${total}` : ""} licences</span>
            {hasMore && <span className="t-muted">Scroll to load more</span>}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateLicenceModal
          onClose={() => setShowCreate(false)}
          onDone={(id) => { setShowCreate(false); navigate(`/dashboard/admin/licences/${id}`); }}
        />
      )}
    </PageShell>
  );
}

function CreateLicenceModal({ onClose, onDone }) {
  const api = useAdminApi();
  const [f, setF] = useState({
    email: "", password: "",
    store_name: "", store_type: "medical",
    store_city: "", store_phone_number: "",
    is_hospital: false, is_store: true, is_lab: false, is_radiology_lab: false,
    max_devices: 1,
  });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const ok = f.email.trim() && f.password.length >= 6;

  const submit = async (e) => {
    e && e.preventDefault();
    setErr(null); setSaving(true);
    try {
      const r = await api.post("admin/licences/", f);
      onDone(r.data.id);
    } catch (e) { setErr(e); } finally { setSaving(false); }
  };

  return (
    <Modal
      title="Create new IMS licence"
      icon={Icon.hospital}
      onClose={onClose}
      desc={<>Auto-generated on save: licence ID (UUID), <code>license_key</code> (customer activation key) and <code>backup_secret</code> (encrypts customer backups). You only need email, password and store info.</>}
      footer={
        <>
          <button className="ad-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="ad-btn primary" disabled={!ok || saving} onClick={submit}>
            <Icon.plus />{saving ? "Creating…" : "Create licence"}
          </button>
        </>
      }
    >
      {err && <ErrorBox error={err} />}
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Email" hint="Customer's login email — must be unique" full>
            <input className="form-control" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required autoFocus />
          </Field>
          <Field label="Password" hint="Hashed on save; 6+ chars recommended">
            <input className="form-control" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} />
          </Field>
          <Field label="Max devices" hint="How many installs can activate this licence">
            <input className="form-control" type="number" min={1} max={20} value={f.max_devices} onChange={(e) => set("max_devices", Number(e.target.value))} />
          </Field>
          <Field label="Store / Clinic name" full>
            <input className="form-control" value={f.store_name} onChange={(e) => set("store_name", e.target.value)} />
          </Field>
          <Field label="Type">
            <select className="form-control" value={f.store_type} onChange={(e) => set("store_type", e.target.value)}>
              <option value="medical">Medical</option>
              <option value="pharmacy">Pharmacy</option>
              <option value="general">General</option>
              <option value="supermarket">Supermarket</option>
              <option value="department_store">Department store</option>
              <option value="grocery">Grocery</option>
              <option value="electronics">Electronics</option>
              <option value="fashion">Fashion</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="City">
            <input className="form-control" value={f.store_city} onChange={(e) => set("store_city", e.target.value)} />
          </Field>
          <Field label="Phone" full>
            <input className="form-control" value={f.store_phone_number} onChange={(e) => set("store_phone_number", e.target.value)} placeholder="+92 3XX XXXXXXX" />
          </Field>
          <div className="ad-divider" />
          <Field label="Modules enabled" hint="Which parts of the IMS this customer can use" full>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Option on={f.is_store}         onClick={() => set("is_store", !f.is_store)}         title="Store (POS, inventory, sales)" />
              <Option on={f.is_hospital}      onClick={() => set("is_hospital", !f.is_hospital)}   title="Hospital (patients, visits)" />
              <Option on={f.is_lab}           onClick={() => set("is_lab", !f.is_lab)}             title="Lab (tests, reports)" />
              <Option on={f.is_radiology_lab} onClick={() => set("is_radiology_lab", !f.is_radiology_lab)} title="Radiology" />
            </div>
          </Field>
        </div>
      </form>
    </Modal>
  );
}
